import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import type { SeededRng } from '@/core/rng';
import type { MapLightData } from '@/core/types/contracts';
import { FxTex } from './textures';
import { rainSpawnPerStep, type WeatherIntensity } from './intensity';
import { ShelterMask, type Eave } from './shelter';

export interface Rect { x: number; y: number; w: number; h: number }
export interface FxLayer {
  step(dt: number, i: WeatherIntensity, view: View): void;
  destroy(): void;
  setReduceMotion?(on: boolean): void;
  /** Live particle counts / accumulators (showcase HUD, tests). */
  stats?(): Record<string, number>;
}

/**
 * Spawn accumulators are bounded: an accumulator is reset whenever the pool is saturated (or the driving
 * intensity is 0), so a long storm never banks phantom spawns that would keep falling after the sky clears.
 */
const alive = <T extends { alive: boolean }>(pool: readonly T[]): number => { let n = 0; for (const p of pool) if (p.alive) n++; return n; };
/** Camera view in world pixels (integer). */
export interface View { sx: number; sy: number; w: number; h: number }

const q = Math.floor; // quantise to the pixel grid

// ------------------------------------------------------------------ rain (streaks + ground splashes + eave drips), screen space
interface Drop { x: number; y: number; len: number; vy: number; alive: boolean; jitter: number }
interface Splash { x: number; y: number; age: number; alive: boolean }
/** A drip falls from an eave (screen y `from`) onto the ground at `y`, then splashes. */
interface Drip { x: number; y: number; from: number; age: number; alive: boolean }
const SPLASH_LIFE = 12, DRIP_FALL = 5, DRIP_LIFE = DRIP_FALL + 10;
/**
 * Streaks are screen-space particles, but the map's `rainShelter` rects are honoured in WORLD space at draw
 * time: a streak pixel is drawn only where its drop's jitter beats the ShelterMask coverage (0 outside, 1 from
 * 8 px inside the eave), so rain stops at the eave with a soft edge, whatever the weather state — `interior-rain`
 * still rains outside the roof. Ground splashes never spawn under a roof; eave drips fall from the mask's
 * exposed bottom edges instead.
 */
export class RainLayer implements FxLayer {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly drops: Drop[] = [];
  private readonly splashes: Splash[] = [];
  private readonly drips: Drip[] = [];
  private readonly mask: ShelterMask;
  private spawnAcc = 0; private splashAcc = 0; private dripAcc = 0;
  private drawnPx = 0; private maskedPx = 0;
  private readonly maxDrops = 240;
  constructor(private readonly scene: Phaser.Scene, private readonly rng: SeededRng, shelters: readonly Rect[], private readonly water: Rect[]) {
    this.g = scene.add.graphics().setScrollFactor(0).setDepth(Depth.WEATHER);
    this.mask = new ShelterMask(shelters);
    for (let i = 0; i < this.maxDrops; i++) this.drops.push({ x: 0, y: 0, len: 8, vy: 10, alive: false, jitter: 0 });
    for (let i = 0; i < 64; i++) this.splashes.push({ x: 0, y: 0, age: 0, alive: false });
    for (let i = 0; i < 48; i++) this.drips.push({ x: 0, y: 0, from: 0, age: 0, alive: false });
  }
  step(_dt: number, i: WeatherIntensity, view: View): void {
    const windDx = i.wind * 2.2;
    this.spawnAcc = i.rain <= 0 ? 0 : this.spawnAcc + rainSpawnPerStep(i.rain, GAME_W, this.maxDrops);
    while (this.spawnAcc >= 1) {
      const d = this.drops.find((d) => !d.alive); if (!d) { this.spawnAcc = 0; break; } this.spawnAcc -= 1;
      d.alive = true; d.x = this.rng.range(-30, GAME_W + 30); d.len = this.rng.int(5, 11); d.vy = (8 + this.rng.range(0, 4)) * i.dropSpeed; d.y = -d.len - this.rng.range(0, 40); d.jitter = this.rng.range(0, 1);
    }
    for (const d of this.drops) { if (!d.alive) continue; d.y += d.vy; d.x += windDx; if (d.y > GAME_H + 4) d.alive = false; }
    // splashes on unsheltered ground (world check), not on water (ripples handle that)
    this.splashAcc = i.splash <= 0 ? 0 : this.splashAcc + i.splash * 5;
    while (this.splashAcc >= 1) {
      const s = this.splashes.find((s) => !s.alive); if (!s) { this.splashAcc = 0; break; } this.splashAcc -= 1;
      const x = this.rng.int(0, GAME_W - 1), y = this.rng.int(0, GAME_H - 1);
      if (this.covered(x + view.sx, y + view.sy)) continue;
      s.alive = true; s.x = x; s.y = y; s.age = 0;
    }
    for (const s of this.splashes) { if (!s.alive) continue; s.age++; if (s.age > SPLASH_LIFE) s.alive = false; }
    // eave drips: from the exposed bottom edges of the shelters on screen, rate ∝ visible eave length
    const eaves = i.splash > 0 && !this.mask.empty ? this.mask.visibleEaves(view) : [];
    const eaveW = eaves.reduce((a, e) => a + e.w, 0);
    this.dripAcc = eaveW <= 0 ? 0 : this.dripAcc + Math.min(2.5, i.splash * (eaveW / 160));
    while (this.dripAcc >= 1) {
      const dr = this.drips.find((d) => !d.alive); if (!dr) { this.dripAcc = 0; break; } this.dripAcc -= 1;
      let pick = this.rng.range(0, eaveW); let e = eaves[eaves.length - 1] as Eave;
      for (const c of eaves) { if (pick < c.w) { e = c; break; } pick -= c.w; }
      const wx = e.x + Math.min(e.w - 1, Math.floor(pick)), wy = e.y + this.rng.int(2, 7);
      if (this.covered(wx, wy)) continue;
      dr.alive = true; dr.x = wx - view.sx; dr.y = wy - view.sy; dr.from = e.y - view.sy; dr.age = 0;
    }
    for (const dr of this.drips) { if (!dr.alive) continue; dr.age++; if (dr.age > DRIP_LIFE) dr.alive = false; }
    this.draw(i, windDx, view);
  }
  /** Under a shelter rect or on water: no splash / drip lands here. */
  private covered(wx: number, wy: number): boolean {
    return this.mask.inside(wx, wy) || this.water.some((r) => inRect(r, wx, wy));
  }
  private draw(i: WeatherIntensity, windDx: number, view: View): void {
    const g = this.g; g.clear(); this.drawnPx = 0; this.maskedPx = 0;
    if (i.rain <= 0 && !this.splashes.some((s) => s.alive) && !this.drops.some((d) => d.alive) && !this.drips.some((d) => d.alive)) return;
    g.fillStyle(0xd6ecf2, 0.5);
    const slant = Math.abs(windDx) > 1 ? Math.sign(windDx) : 0;
    for (const d of this.drops) {
      if (!d.alive) continue; const x = q(d.x), y = q(d.y);
      if (slant) { const half = q(d.len / 2); this.column(x - slant, y, half, d.jitter, view); this.column(x, y + half, d.len - half, d.jitter, view); }
      else this.column(x, y, d.len, d.jitter, view);
    }
    for (const s of this.splashes) { if (s.alive) this.splash(q(s.x), q(s.y), s.age); }
    for (const dr of this.drips) {
      if (!dr.alive) continue; const x = q(dr.x);
      if (dr.age <= DRIP_FALL) { g.fillStyle(0xeaf6fa, 0.75); g.fillRect(x, q(dr.from + (dr.y - dr.from) * (dr.age / DRIP_FALL)) - 2, 1, 2); }
      else this.splash(x, q(dr.y), dr.age - DRIP_FALL);
    }
  }
  /** One vertical streak column at screen (x, y0..y0+n), clipped by the shelter mask in world space. */
  private column(x: number, y0: number, n: number, jitter: number, view: View): void {
    if (n <= 0 || x < 0 || x >= GAME_W || y0 >= GAME_H || y0 + n <= 0) return; // off-screen (drops spawn/drift 30 px past the edges)
    const g = this.g;
    if (this.mask.empty) { g.fillRect(x, y0, 1, n); this.drawnPx += n; return; }
    const wx = x + view.sx, wy0 = y0 + view.sy;
    const cTop = this.mask.coverage(wx, wy0), cMid = this.mask.coverage(wx, wy0 + (n >> 1)), cBot = this.mask.coverage(wx, wy0 + n - 1);
    if (cTop <= 0 && cMid <= 0 && cBot <= 0) { g.fillRect(x, y0, 1, n); this.drawnPx += n; return; }
    if (cTop >= 1 && cMid >= 1 && cBot >= 1) { this.maskedPx += n; return; }
    let run = -1; // the streak crosses an eave: per-pixel, drawn as runs
    for (let k = 0; k < n; k++) {
      const c = k === 0 ? cTop : k === n - 1 ? cBot : this.mask.coverage(wx, wy0 + k);
      if (c <= 0 || jitter >= c) { if (run < 0) run = k; this.drawnPx++; }
      else { if (run >= 0) { g.fillRect(x, y0 + run, 1, k - run); run = -1; } this.maskedPx++; }
    }
    if (run >= 0) g.fillRect(x, y0 + run, 1, n - run);
  }
  private splash(x: number, y: number, age: number): void {
    const g = this.g;
    if (age < 4) { g.fillStyle(0xeaf6fa, 0.7); g.fillRect(x, y, 1, 1); }
    else if (age < 8) { g.fillStyle(0xeaf6fa, 0.6); g.fillRect(x - 1, y - 1, 1, 1); g.fillRect(x + 1, y - 1, 1, 1); g.fillRect(x, y, 1, 1); }
    else { g.fillStyle(0xeaf6fa, 0.35); g.fillRect(x - 2, y - 2, 1, 1); g.fillRect(x + 2, y - 2, 1, 1); g.fillRect(x - 1, y, 1, 1); g.fillRect(x + 1, y, 1, 1); }
  }
  /** `rainDrawn` / `rainMasked`: streak pixels drawn vs hidden by shelters in the last draw. */
  stats(): Record<string, number> {
    return { drops: alive(this.drops), splashes: alive(this.splashes), drips: alive(this.drips), spawnAcc: this.spawnAcc, splashAcc: this.splashAcc, dripAcc: this.dripAcc, rainDrawn: this.drawnPx, rainMasked: this.maskedPx };
  }
  destroy(): void { this.g.destroy(); }
}

// ------------------------------------------------------------------ wind leaves, screen space
interface Leaf { x: number; y: number; vx: number; vy: number; phase: number; alive: boolean; kind: number }
export class LeafLayer implements FxLayer {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly leaves: Leaf[] = [];
  private acc = 0;
  constructor(scene: Phaser.Scene, private readonly rng: SeededRng) {
    this.g = scene.add.graphics().setScrollFactor(0).setDepth(Depth.WEATHER - 1);
    for (let i = 0; i < 48; i++) this.leaves.push({ x: 0, y: 0, vx: 0, vy: 0, phase: 0, alive: false, kind: 0 });
  }
  step(_dt: number, i: WeatherIntensity, _view: View): void {
    this.acc = i.leaves <= 0 ? 0 : this.acc + i.leaves * 0.35;
    const dir = i.wind >= 0 ? 1 : -1;
    while (this.acc >= 1) {
      const l = this.leaves.find((l) => !l.alive); if (!l) { this.acc = 0; break; } this.acc -= 1;
      l.alive = true; l.kind = this.rng.int(0, 1); l.phase = this.rng.range(0, Math.PI * 2);
      if (this.rng.chance(0.6)) { l.x = dir > 0 ? -6 : GAME_W + 6; l.y = this.rng.range(-10, GAME_H * 0.7); } else { l.x = this.rng.range(0, GAME_W); l.y = -4; }
      l.vx = 0; l.vy = 0;
    }
    for (const l of this.leaves) {
      if (!l.alive) continue; l.phase += 0.09;
      l.vx = i.wind * 3.2 + Math.sin(l.phase) * 0.7; l.vy = 0.55 + Math.cos(l.phase * 0.7) * 0.5;
      l.x += l.vx; l.y += l.vy;
      if (l.x < -12 || l.x > GAME_W + 12 || l.y > GAME_H + 6) l.alive = false;
    }
    const g = this.g; g.clear();
    for (const l of this.leaves) {
      if (!l.alive) continue; const x = q(l.x), y = q(l.y); const flip = Math.sin(l.phase) > 0;
      g.fillStyle(flip ? 0x8ec95a : 0x3c7a3a, 0.95); g.fillRect(x, y, 3, 2); g.fillStyle(flip ? 0x3c7a3a : 0x8ec95a, 0.95); g.fillRect(x + (l.kind ? 2 : 0), y, 1, 1);
    }
  }
  stats(): Record<string, number> { return { leaves: alive(this.leaves), leafAcc: this.acc }; }
  destroy(): void { this.g.destroy(); }
}

// ------------------------------------------------------------------ water shimmer + rain ripples, world space (6 fps)
interface Ripple { x: number; y: number; r: number; alive: boolean }
export class WaterLayer implements FxLayer {
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly ripples: Ripple[] = [];
  private readonly dashes: { x: number; y: number; w: number; phase: number; rect: number }[] = [];
  private frame = 0; private tick = 0; private acc = 0;
  constructor(scene: Phaser.Scene, private readonly rng: SeededRng, private readonly rects: Rect[]) {
    this.g = scene.add.graphics().setDepth(Depth.GROUND_DETAIL + 1);
    rects.forEach((r, ri) => { const n = Math.max(3, Math.round((r.w * r.h) / 900)); for (let k = 0; k < n; k++) this.dashes.push({ x: r.x + rng.int(2, Math.max(2, r.w - 8)), y: r.y + rng.int(1, Math.max(1, r.h - 2)), w: rng.int(3, 6), phase: rng.int(0, 3), rect: ri }); });
    for (let i = 0; i < 48; i++) this.ripples.push({ x: 0, y: 0, r: 0, alive: false });
  }
  step(_dt: number, i: WeatherIntensity, view: View): void {
    if (!this.rects.length) return;
    this.acc = i.ripples <= 0 ? 0 : this.acc + i.ripples * 0.35 * Math.min(3, this.rects.reduce((a, r) => a + (r.w * r.h) / 8192, 0));
    while (this.acc >= 1) {
      const rp = this.ripples.find((r) => !r.alive); if (!rp) { this.acc = 0; break; } this.acc -= 1;
      const r = this.rng.pick(this.rects); rp.alive = true; rp.x = r.x + this.rng.int(3, Math.max(3, r.w - 4)); rp.y = r.y + this.rng.int(2, Math.max(2, r.h - 3)); rp.r = 0;
    }
    this.tick++;
    if (this.tick % 10 !== 0) return; // 6 fps water animation
    this.frame = (this.frame + 1) & 3;
    for (const rp of this.ripples) { if (!rp.alive) continue; rp.r += 1.5; if (rp.r > 7) rp.alive = false; }
    this.draw(view);
  }
  private draw(view: View): void {
    const g = this.g; g.clear();
    const visible = (r: Rect) => r.x < view.sx + view.w && r.x + r.w > view.sx && r.y < view.sy + view.h && r.y + r.h > view.sy;
    const vis = this.rects.map(visible);
    for (const d of this.dashes) {
      if (!vis[d.rect]) continue; const f = (d.phase + this.frame) & 3; if (f === 3) continue;
      g.fillStyle(f === 1 ? 0xbef2ec : 0x6fd3c8, f === 1 ? 0.55 : 0.4); g.fillRect(q(d.x) + (f === 2 ? 1 : 0), q(d.y), d.w - (f === 2 ? 1 : 0), 1);
    }
    g.fillStyle(0xd8f6f2, 0.7);
    for (const rp of this.ripples) {
      if (!rp.alive) continue; const n = 8 + Math.round(rp.r * 2); const rect = this.rects.find((r) => inRect(r, rp.x, rp.y));
      for (let k = 0; k < n; k++) { const a = (k / n) * Math.PI * 2; const px = q(rp.x + Math.cos(a) * rp.r), py = q(rp.y + Math.sin(a) * rp.r * 0.5); if (!rect || inRect(rect, px, py)) g.fillRect(px, py, 1, 1); }
    }
  }
  stats(): Record<string, number> { return { ripples: alive(this.ripples), rippleAcc: this.acc }; }
  destroy(): void { this.g.destroy(); }
}

// ------------------------------------------------------------------ lantern glow pools, world space, 4 fps flicker
/**
 * Depth decision: the glow is an ADDITIVE bloom, not a lit-ground decal, so it lives at Depth.LIGHTING —
 * above OVERHEAD roofs and the WEATHER rain streaks on purpose (a lantern under an eave bleeds light onto the
 * roof rim and lights the rain in front of it). Occluded ground pools would need a per-light mask against the
 * overhead layer; not worth it at 32 px. Keep this band unless the art direction asks for masked pools.
 */
export class LanternLayer implements FxLayer {
  private readonly images: { img: Phaser.GameObjects.Image; data: MapLightData; base: number }[] = [];
  private tick = 0;
  constructor(scene: Phaser.Scene, private readonly rng: SeededRng, lights: MapLightData[]) {
    for (const l of lights) {
      const color = Phaser.Display.Color.HexStringToColor(l.color || '#ffb347').color;
      const img = scene.add.image(q(l.x), q(l.y), FxTex.glow).setBlendMode(Phaser.BlendModes.ADD).setTint(color).setDepth(Depth.LIGHTING).setAlpha(Math.min(1, l.intensity) * 0.75);
      img.setScale(Math.max(0.25, l.radius / 32));
      this.images.push({ img, data: l, base: Math.min(1, l.intensity) * 0.75 });
    }
  }
  step(_dt: number, i: WeatherIntensity, _view: View): void {
    this.tick++;
    if (this.tick % 15 !== 0) return; // 4 fps
    const rainBoost = 1 + i.darken * 0.35; // pools read stronger under a dark sky
    for (const e of this.images) {
      if (!e.data.flicker) { e.img.setAlpha(Math.min(1, e.base * rainBoost)); continue; }
      const f = 0.82 + this.rng.range(0, 0.18);
      e.img.setAlpha(Math.min(1, e.base * f * rainBoost));
      e.img.setScale(Math.max(0.25, (e.data.radius + (this.rng.chance(0.3) ? 1 : 0)) / 32));
    }
  }
  destroy(): void { for (const e of this.images) e.img.destroy(); this.images.length = 0; }
}

// ------------------------------------------------------------------ heat haze (camera displacement filter), subtle
export class HazeLayer implements FxLayer {
  private filter: Phaser.Filters.Displacement | null = null;
  private t = 0; private reduce = false;
  constructor(private readonly scene: Phaser.Scene) {
    try {
      const cam = scene.cameras.main as Phaser.Cameras.Scene2D.Camera & { filters?: { internal: { addDisplacement(t?: string, x?: number, y?: number): Phaser.Filters.Displacement; remove(f: Phaser.Filters.Controller): unknown } } };
      if (cam.filters && scene.textures.exists(FxTex.noise)) { this.filter = cam.filters.internal.addDisplacement(FxTex.noise, 0, 0); this.filter.setActive(false); }
    } catch (e) { console.warn('[effects] haze filter unavailable', e); this.filter = null; }
  }
  setReduceMotion(on: boolean): void { this.reduce = on; }
  step(dt: number, i: WeatherIntensity, _view: View): void {
    if (!this.filter) return;
    this.t += dt / 1000;
    const strength = this.reduce ? 0 : i.haze;
    if (strength <= 0.01) { if (this.filter.active) this.filter.setActive(false); return; }
    if (!this.filter.active) this.filter.setActive(true);
    this.filter.x = strength * 0.0022 * Math.sin(this.t * 0.9);
    this.filter.y = strength * 0.0014 * Math.sin(this.t * 1.37 + 1.1);
  }
  destroy(): void {
    if (!this.filter) return;
    try { const cam = this.scene.cameras.main as Phaser.Cameras.Scene2D.Camera & { filters?: { internal: { remove(f: Phaser.Filters.Controller): unknown } } }; cam.filters?.internal.remove(this.filter); } catch { /* scene gone */ }
    this.filter = null;
  }
}

// ------------------------------------------------------------------ lightning flash (screen space) + thunder cue
const FLASH_FRAMES = [0.85, 0.5, 0.05, 0.0, 0.6, 0.35, 0.15, 0.0];
export class LightningLayer implements FxLayer {
  private readonly rect: Phaser.GameObjects.Rectangle;
  private seq: number[] = []; private hold = 0; private reduce = false;
  private thunderIn = -1;
  constructor(scene: Phaser.Scene, private readonly rng: SeededRng, private readonly onThunder: (volume: number) => void) {
    this.rect = scene.add.rectangle(0, 0, GAME_W, GAME_H, 0xf4f8ff).setOrigin(0).setScrollFactor(0).setDepth(Depth.WEATHER + 5).setAlpha(0);
  }
  setReduceMotion(on: boolean): void { this.reduce = on; }
  flash(): void {
    if (this.seq.length) return;
    this.seq = this.reduce ? [0.3, 0.3, 0.15, 0] : [...FLASH_FRAMES]; this.hold = 0;
    this.thunderIn = this.rng.int(35, 110); // steps ≈ 0.6–1.8 s: distance cue
  }
  step(_dt: number, i: WeatherIntensity, _view: View): void {
    if (this.seq.length === 0 && i.lightning > 0 && this.rng.chance(i.lightning / 60)) this.flash();
    if (this.seq.length) { if (this.hold <= 0) { this.rect.setAlpha(this.seq.shift() ?? 0); this.hold = 2; } this.hold--; if (!this.seq.length) this.rect.setAlpha(0); }
    if (this.thunderIn > 0) this.thunderIn--; else if (this.thunderIn === 0) { this.thunderIn = -1; this.onThunder(0.55 + this.rng.range(0, 0.35)); }
  }
  destroy(): void { this.rect.destroy(); }
}

export function inRect(r: Rect, x: number, y: number): boolean { return x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h; }
